import React, { useState } from 'react';
import { Box, TextField, Button, Paper, Typography, Alert, Link } from '@mui/material';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import api from '../api';
import AdminLayout from '../components/AdminLayout';
import SessionReplacementDialog, { SessionReplacementProof, SessionReplacementProofType } from '../components/SessionReplacementDialog';

const AdminLogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [secretCode, setSecretCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [replacementChallenge, setReplacementChallenge] = useState<string | null>(null);
  const [replacementProofType, setReplacementProofType] = useState<SessionReplacementProofType>('password');
  
  const navigate = useNavigate();
  const location = useLocation();
  // NOTE: Admin login stores credentials separately so it doesn't affect public site auth
  const from = location.state?.from?.pathname || '/admin';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const res = await api.post('/admin/auth/login', {
        email: email.trim(),
        password: password.trim(),
        secretCode: secretCode.trim()
      });

      if (res.status === 409 && res.data?.code === 'SESSION_REPLACEMENT_REQUIRED') {
        setReplacementChallenge(res.data.challengeToken);
        setReplacementProofType(res.data.proofType || 'password');
        return;
      }
      if (!res.data?.success) {
        throw new Error(res.data?.message || 'Login failed');
      }

      const { token, data: user } = res.data;
      if (!token || !user) {
        throw new Error('Invalid login response');
      }

      // CRITICAL: Clear all public user auth before storing admin session
      // This prevents a previous non-admin login from interfering
      try {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
      } catch (e) {
        // ignore any errors during cleanup
      }

      // Store admin session separately from public site auth
      localStorage.setItem('admin_auth_token', token);
      localStorage.setItem('admin_user', JSON.stringify(user));

      // Ensure any previous per-tab session unlocks are cleared for a fresh admin session
      try {
        const keysToRemove: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const k = sessionStorage.key(i);
          if (k && k.startsWith('admin_tab_pw_')) keysToRemove.push(k);
        }
        keysToRemove.forEach(k => sessionStorage.removeItem(k));
      } catch (e) {
        // ignore
      }

      // Navigate to admin dashboard with success message and fresh login flag
      navigate(from, {
        replace: true,
        state: { success: `Welcome, ${user.name || 'Admin'}`, freshLogin: true }
      });
    } catch (err: any) {
      if (err.response?.data?.code === 'SESSION_REPLACEMENT_REQUIRED') {
        setReplacementChallenge(err.response.data.challengeToken);
        setReplacementProofType(err.response.data.proofType || 'password');
      } else {
        setError(err.response?.data?.message || err.message || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const confirmReplacement = async (proof: SessionReplacementProof) => {
    if (!replacementChallenge) return;
    setError(null);
    setLoading(true);
    try {
      const res = await api.post('/auth/confirm-session-replacement', { challengeToken: replacementChallenge, ...proof });
      const { token, data: user } = res.data || {};
      if (!token || !user) throw new Error('Invalid login response');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      localStorage.setItem('admin_auth_token', token);
      localStorage.setItem('admin_user', JSON.stringify(user));
      setReplacementChallenge(null);
      navigate(from, { replace: true, state: { success: `Welcome, ${user.name || 'Admin'}`, freshLogin: true } });
    } catch (err: any) {
      if (err.response?.data?.code === 'SESSION_REPLACEMENT_REQUIRED') {
        setReplacementChallenge(err.response.data.challengeToken);
        setReplacementProofType(err.response.data.proofType || replacementProofType);
        setError(null);
      } else if (err.response?.data?.code === 'INVALID_CONFIRMATION_CREDENTIALS') {
        setError(err.response.data.message || 'The password is incorrect.');
      } else if (err.response?.data?.code === 'SESSION_REPLACEMENT_EXPIRED') {
        setReplacementChallenge(null);
        setError(err.response.data.message || 'The sign-in confirmation expired. Please sign in again.');
      } else {
        setError(err.response?.data?.message || err.message || 'Could not sign in on this device');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
          minHeight: 'calc(100vh - 64px)', // Account for AppBar height
          bgcolor: 'background.default'
        }}
      >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 400,
          width: '100%'
        }}
      >
        <Typography variant="h5" component="h1" gutterBottom align="center">
          Admin Login
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            label="Email"
            type="email"
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
          />

          <TextField
            label="Password"
            type="password"
            fullWidth
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={loading}
          />

          <TextField
            label="Admin Secret Code"
            type="password"
            fullWidth
            margin="normal"
            value={secretCode}
            onChange={(e) => setSecretCode(e.target.value)}
            required
            disabled={loading}
            helperText="Required for admin access"
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{ mt: 3 }}
            disabled={loading}
          >
            {loading ? 'Logging in...' : 'Login'}
          </Button>
        </form>
        
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            Don't have an admin account?{' '}
            <Link
              component={RouterLink}
              to="/admin/signup"
              sx={{ 
                textDecoration: 'none', 
                color: 'primary.main',
                '&:hover': { textDecoration: 'underline' } 
              }}
            >
              Create an Account
            </Link>
          </Typography>
        </Box>
      </Paper>
    </Box>
      <SessionReplacementDialog
        open={Boolean(replacementChallenge)}
        proofType={replacementProofType}
        loading={loading}
        error={error}
        onConfirm={confirmReplacement}
        onCancel={() => { setReplacementChallenge(null); setError(null); }}
      />
    </AdminLayout>
  );
};

export default AdminLogin;